// Ranked alternates (D16, CR-028): his rule keeps alternates active until the primary accepts. The Recruiter orders them
// with the keyboard-reachable up and down buttons; every reorder writes the whole contiguous order
// (debrief.order_alternates), so a rank never has a gap.
import { useState } from "react";
import { Banner, DEBRIEF_ROOM_LABELS, DEBRIEF_OUTCOME_LABELS, RankedList, Stack } from "@/mc";
import type { RankedItem } from "@/mc";
import { debriefOrderAlternates, type DebriefOpenOutput } from "@/api/tools";
import { errorText, useWrite } from "@/api/useTool";
import type { RoomPanelProps } from "@/views/b7/room/types";
import { candidateName, memberOf, roomOf, ROOM_KEYS } from "./labels";

/** The alternates in rank order, as the list's rows. */
export function alternateItems(view: DebriefOpenOutput): RankedItem[] {
  return [...(roomOf(view)?.alternates ?? [])]
    .sort((a, b) => a.rank - b.rank)
    .map((a) => ({ key: a.application_id, name: candidateName(memberOf(view, a.application_id), a.application_id), candidate: true, hueKey: a.application_id }));
}

export function Alternates({ view, onChanged }: RoomPanelProps & { scorecardId?: string | undefined }) {
  const write = useWrite(debriefOrderAlternates, ROOM_KEYS);
  const [refused, setRefused] = useState<string | null>(null);
  const items = alternateItems(view);
  if (items.length === 0) return null;
  const edit = view.can.order_alternates !== false && items.length > 1;
  const hs: Record<string, readonly [string | undefined, string | undefined]> = {};
  if (items[0]) hs[items[0].key] = [undefined, "room.alt.down"];
  if (items[1]) hs[items[1].key] = ["room.alt.up", undefined];
  const reorder = async (next: RankedItem[]) => {
    setRefused(null);
    try {
      await write.mutateAsync({ requisition_id: view.requisition_id, application_ids: next.map((i) => i.key) });
      onChanged();
    } catch (e) {
      setRefused(errorText(e));
    }
  };
  return (
    <Stack gap={8}>
      <RankedList
        title={DEBRIEF_OUTCOME_LABELS.alternates}
        items={items}
        mode={edit ? "edit" : "view"}
        hs={hs}
        onReorder={(next) => void reorder(next)}
      />
      <Banner tone="info">{DEBRIEF_ROOM_LABELS.activeUntilPrimaryAccepts}</Banner>
      {refused ? <Banner tone="red" live>{refused}</Banner> : null}
    </Stack>
  );
}
